/**
 * @file        src/pages/package/AddCategory.jsx
 * @description Create / edit a package category. Field-level validation via zod
 *              (mirrors the server schema in routes/packageCategories.routes.js).
 *              Reused for both /package/categories/new and .../:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (Package Management slice 1).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';

const schema = z.object({
  name: z.string().trim().min(1, 'Required').max(120),
  slug: z.string().trim().max(140).optional(),
  description: z.string().trim().max(500).optional(),
  isActive: z.boolean(),
});

const empty = { name: '', slug: '', description: '', isActive: true };

export default function AddCategory() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data: existing } = useQuery({
    queryKey: qk.packageCategories.one(id),
    queryFn: () => get(`/package-categories/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const c = existing.data;
      setForm({ name: c.name, slug: c.slug, description: c.description || '', isActive: !!c.is_active });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (body) => (isEdit ? patch(`/package-categories/${id}`, body) : post('/package-categories', body)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Category updated' : 'Category added', `${form.name} saved.`);
      qc.invalidateQueries({ queryKey: ['package-categories'] });
      navigate('/package/categories');
    },
    onError: (err) => {
      const fields = err.response?.data?.error?.fields;
      if (fields) setErrors(fields);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fieldErrs = {};
      for (const i of parsed.error.issues) fieldErrs[i.path.join('.')] = i.message;
      return setErrors(fieldErrs);
    }
    setErrors({});
    // Drop empty optional strings so the server treats them as absent.
    const body = { ...parsed.data };
    if (!body.slug) delete body.slug;
    if (!body.description) delete body.description;
    mut.mutate(body);
  }

  function onChange(key) {
    return (e) => setForm({ ...form, [key]: e.target.value });
  }

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Category' : 'Add Package Category'}
        subtitle="Package Management"
        actions={<button className="btn ghost" onClick={() => navigate('/package/categories')}><Icon name="x" /> Cancel</button>}
      />

      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Category Name" required err={errors.name}>
            <input value={form.name} onChange={onChange('name')} placeholder="e.g. Honeymoon" />
          </Field>
          <Field label="Slug" err={errors.slug} hint="Auto-generated from the name if left blank.">
            <input value={form.slug} onChange={onChange('slug')} placeholder="e.g. honeymoon" />
          </Field>
          <Field label="Description" err={errors.description} full>
            <textarea rows={3} value={form.description} onChange={onChange('description')}
                      placeholder="Optional short description" />
          </Field>
          <Field label="Status">
            <label className="check">
              <input type="checkbox" checked={form.isActive}
                     onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              <span>Active (visible to the website)</span>
            </label>
          </Field>

          <div className="form-actions">
            {!isEdit && (
              <button type="button" className="btn ghost" onClick={() => setForm(empty)}>Reset</button>
            )}
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Category'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, hint, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
